import React from "react";
import { Card } from "react-bootstrap";
import ReportTableFrame from "@core/components/ReportTableFrame";
import type {
  FinanceMeta,
  FinanceStatisticsSections,
  GeneralInformationEntry,
  StatLeaf,
} from "@core/reports/api/fetchFinanceReport";
/**
 * Read-only replica of the paper "Monthly Local Church Report Form" — Finance &
 * Statistics sheet. Four sections matching the form: General Information, Monthly
 * Membership Analysis, Finances to the Nation, Operational Activities
 * Checklist. Reused by ChurchFinanceStatisticsPage and the per-church eyeball modal.
 */

type Props = {
  data: FinanceStatisticsSections & { meta?: FinanceMeta };
  /** Accepted for call-site compatibility (preview modal); no on-screen org header. */
  showHeader?: boolean;
};

function formatMoney(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Operational/membership leaf values arrive as strings; render booleans as Yes/No. */
function formatValue(value: StatLeaf["value"]): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === "true") return "Yes";
  if (value === "false") return "No";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
}

const FinanceStatisticsView: React.FC<Props> = ({ data }) => {
  const { general_information, monthly_membership_analysis, nation_office_finances, operational_activities_checklist } = data;

  const generalRows: Array<[string, GeneralInformationEntry]> = Object.entries(general_information ?? {});
  const hasAttendance = generalRows.some(([, e]) => !!e.total_attendance);

  return (
    <div className="ministers-report-view">
      {/* GENERAL INFORMATION — $ and Total Attendance columns */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">General Information</Card.Header>
        <Card.Body className="p-0">
          <ReportTableFrame>
            <table className="ntcog-report-table">
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col" style={{ width: 140 }}>$</th>
                  {hasAttendance ? <th scope="col" style={{ width: 140 }}>Total Attendance</th> : null}
                </tr>
              </thead>
              <tbody>
                {generalRows.map(([key, entry]) => (
                  <tr key={key}>
                    <td>{entry.label}</td>
                    <td className="fw-semibold">{formatMoney(entry.dollar?.value ?? null)}</td>
                    {hasAttendance ? (
                      <td>{entry.total_attendance ? formatValue(entry.total_attendance.value) : ""}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportTableFrame>
        </Card.Body>
      </Card>

      {/* MONTHLY MEMBERSHIP ANALYSIS */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">Monthly Membership Analysis</Card.Header>
        <Card.Body className="p-0">
          <ReportTableFrame>
            <table className="ntcog-report-table">
              <tbody>
                {(monthly_membership_analysis?.rows ?? []).map((row, i) => (
                  <tr key={`${row.kpi_id ?? i}`}>
                    <td>{row.label}</td>
                    <td className="fw-semibold">{formatValue(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportTableFrame>
        </Card.Body>
      </Card>

      {/* FINANCES TO THE NATION */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">Finances to the Nation</Card.Header>
        <Card.Body className="p-0">
          <ReportTableFrame>
            <table className="ntcog-report-table">
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col" style={{ width: 140 }}>$</th>
                </tr>
              </thead>
              <tbody>
                {(nation_office_finances?.rows ?? []).map((row, i) => (
                  <tr key={`${row.kpi_id ?? i}`}>
                    <td>{row.label}</td>
                    <td className="fw-semibold">{formatMoney(row.dollar?.value ?? null)}</td>
                  </tr>
                ))}
                {nation_office_finances?.total ? (
                  <tr className="ntcog-rt-row--total">
                    <td>{nation_office_finances.total.label ?? "TOTAL"}</td>
                    <td>{formatMoney(nation_office_finances.total.dollar?.value ?? null)}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </ReportTableFrame>
        </Card.Body>
      </Card>

      {/* OPERATIONAL ACTIVITIES CHECKLIST */}
      <Card className="mb-3 ntcog-card">
        <Card.Header className="fw-semibold">Operational Activities Checklist</Card.Header>
        <Card.Body className="p-0">
          <ReportTableFrame>
            <table className="ntcog-report-table">
              <tbody>
                {(operational_activities_checklist?.rows ?? []).map((row, i) => (
                  <tr key={`${row.kpi_id ?? i}`}>
                    <td>{row.label}</td>
                    <td className="fw-semibold" style={{ whiteSpace: "pre-wrap" }}>{formatValue(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportTableFrame>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FinanceStatisticsView;
